import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApiEu} from '@di-shared/api/di';
import {parseDateWithFormat} from '@shared/dateUtils';
import React from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ERROR, UNIT} from '@di-shared/const';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';

export interface SafeDrivingSectionEuProps {
  vin: string;
}

function SafeDrivingSectionEu({vin}: SafeDrivingSectionEuProps) {
  const {t} = useInferableTranslation();

  const {data: ubiData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.safeDriving.latest), vin],
    queryFn: () => diApiEu.safeDriving.latest({vin}),
    select: ({data}) => {
      return {
        ...data,
        latestScoreInfo: {
          ...data.latestScoreInfo,
          lastUpdatedDate: parseDateWithFormat(data.latestScoreInfo.lastUpdatedDate),
        },
      };
    },
  });

  return (
    <div className="section section_safe_score type_eu">
      <div className="section_container">
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
          aria-label={t('ctn_home_Learnmoreabout')}
        />
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_home_dss_title')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">{ubiData.latestScoreInfo.drivingSafetyScore}</span>
            <span className="safe_score_unit">{t('ctn_home_dss_points')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_home_dss_compared_02_$n', {
                n:
                  ubiData.latestScoreInfo.previousScoreDelta === 0
                    ? ubiData.latestScoreInfo.previousScoreDelta
                    : ubiData.latestScoreInfo.previousScoreDelta > 0
                      ? `+${ubiData.latestScoreInfo.previousScoreDelta}`
                      : `-${ubiData.latestScoreInfo.previousScoreDelta}`,
              })}
            </span>
          </span>
        </div>

        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_home_dss_top_percent_$nn')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ubiData.latestScoreInfo.rank}</span>
              <span className="statistics_info_unit">{UNIT.PERCENT}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">
              {ubiData.latestScoreInfo.drivingSafetyScore === 100
                ? t('ctn_dss_LN_top_score')
                : t('ctn_dss_LN_until_n_percent_$percent', {percent: ubiData.latestScoreInfo.nextRank})}
            </span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">
                {ubiData.latestScoreInfo.drivingSafetyScore === 100
                  ? t('ctn_dss_LN_great_work')
                  : ubiData.latestScoreInfo.drivingSafetyScore === 0
                    ? t('ctn_dss_LN_just_drive')
                    : t('ctn_dss_LN_remaining_n_points_$score', {score: ubiData.latestScoreInfo.remainingPointToNextRank})}
              </span>
            </span>
          </li>
        </ul>

        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item">
              <span
                className="ic ic_drive_distance"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span
                  className="score_summary_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('ctn_home_dss_estimated')}
                </span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.estimatedAnnualMileage})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_warning_break_oil"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_dss_hard_braking')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.hardBraking})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_avgspeed"
                aria-hidden="true"
              />
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_average_speed')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.averageTripSpeed})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_maxspeed"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_maximum_speed')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.maximumHighSpeed})}
                </span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_time"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_driving_hours')}</span>
                <span className="score_summary_value">
                  {t('imsi_home_p', {n: ubiData.latestScoreInfo.subScoreDetailResponse.drivingTimeOfDay})}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingSectionEuLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="section section_safe_score type_eu skeleton">
      <div className="section_container">
        <div className="section_container_full_btn ">
          <span className="blind">{t('ctn_home_Learnmoreabout')}</span>
        </div>
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_home_dss_title')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">100</span>
            <span className="safe_score_unit">{t('ctn_home_dss_title')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_home_dss_compared_02_$n', {
                n: 0,
              })}
            </span>
          </span>
        </div>

        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_home_dss_top_percent_$nn')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{10}</span>
              <span className="statistics_info_unit">{UNIT.PERCENT}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">100%</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{10}</span>
              <span className="statistics_info_unit">P</span>
            </span>
          </li>
        </ul>

        <div className="safe_score_summary">
          <ul
            className="score_summary_list"
            style={{margin: 0}}
          >
            <li className="score_summary_item">
              <span
                className="ic ic_drive_distance"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span
                  className="score_summary_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('ctn_home_dss_estimated')}
                </span>
                <span className="score_summary_value">{t('imsi_home_p', {n: 10})}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingSectionEuError({vin}: SafeDrivingSectionEuProps) {
  const {t} = useInferableTranslation();

  return (
    <div className="section section_safe_score type_eu">
      <div className="section_container">
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
        >
          <span className="blind">{t('ctn_home_Learnmoreabout')}</span>
        </LinkTo>
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_home_dss_title')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_unit">{t('ctn_home_dss_title')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_home_dss_compared_02_$n', {
                n: ERROR.DASH,
              })}
            </span>
          </span>
        </div>

        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_home_dss_top_percent_$nn')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.PERCENT}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('ctn_dss_LN_until_n_percent_$percent', {percent: ERROR.DASH})}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{t('ctn_dss_LN_remaining_n_points_$score', {score: ERROR.DASH})}</span>
            </span>
          </li>
        </ul>

        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item">
              <span
                className="ic ic_drive_distance"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span
                  className="score_summary_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('ctn_home_dss_estimated')}
                </span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_warning_break_oil"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_dss_hard_braking')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_avgspeed"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_average_speed')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_maxspeed"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_maximum_speed')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span
                className="ic ic_time"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_home_driving_hours')}</span>
                <span className="score_summary_value">{t('imsi_home_p', {n: ERROR.DASH})}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// FALLBACK(LOADING, ERROR) UI
SafeDrivingSectionEu.Error = ({vin}: {vin: string}) => <SafeDrivingSectionEuError vin={vin} />;
SafeDrivingSectionEu.Loading = () => <SafeDrivingSectionEuLoading />;

export default SafeDrivingSectionEu;
